import React, { useMemo } from "react";
import { useProfile } from '../../context/ProfileContext';
import UserProfilePicture from './UserProfilePicture';
import { useNavigate } from "react-router-dom";

// ✅ 1. The component now correctly receives `isOpen` and `accounts` from its parent.
const SwitchAccountsModal = ({ isOpen, accounts = [], onClose, onSelectAccount }) => {
  // ✅ 2. All hooks are now called at the top, before any conditions.
  const { activeProfile, isDarkMode } = useProfile();
  const navigate = useNavigate()

  // ✅ 2. New sorting logic
  const sortedAccounts = useMemo(() => {
    // Separate the user profile from the artist profiles
    const userProfile = accounts.find(acc => acc.type === 'user');
    const artistProfiles = accounts.filter(acc => 
      acc.type === 'artist' && 
      acc.isDeleted !== true // 👈 ONLY show active artists
    );

    // Sort artist profiles alphabetically by name
    artistProfiles.sort((a, b) => {
      const nameA = a.artistName || '';
      const nameB = b.artistName || '';
      return nameA.localeCompare(nameB);
    });
    
    // Return the user profile first, followed by the sorted artists
    return userProfile ? [userProfile, ...artistProfiles] : artistProfiles;
  }, [accounts]);

  // ✅ 3. The check to render nothing now comes AFTER the hooks.
  if (!isOpen) {
    return null;
  }

  const filteredAccounts = sortedAccounts.filter(
    (account) => account.id !== activeProfile?.id
  );

  const handleSwitchAccount = (account) => {
    onSelectAccount(account);
    
    const targetTab = account.type === 'artist' ? 'discography' : 'library';
    navigate(`/profile?tab=${targetTab}`, { replace: true });
    
    onClose();
  };

  return (
    <div 
      // ✅ Added padding here to create margins on the left/right of the modal on small screens
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4 transition-opacity duration-300"
      onClick={onClose}
    >
      <div 
        className={`
          rounded-3xl p-8 max-w-md w-full shadow-2xl transition-all duration-500
          ${isDarkMode 
            ? "bg-[#1a1a1a] border border-white/40 shadow-[0_20px_50px_rgba(0,0,0,0.5)]" 
            : "bg-white border border-slate-100"
          }
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-6 text-center dark:text-brand-white">Switch Accounts</h2>
        {/* ✅ 3. This new container makes the list of accounts scrollable */}
        <div className="overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
          {filteredAccounts.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-6">
              {filteredAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex flex-col items-center cursor-pointer w-24"
                  onClick={() => handleSwitchAccount(account)}
                >
                  <UserProfilePicture 
                    photoURL={account.photoURL} 
                    sizeClass="w-16 h-16" 
                    isPremium={account.type === 'user' && account.premiumSubscription?.status === 'active'}
                  />
                  <p className="text-sm text-center mt-2 break-words text-gray-900 dark:text-brand-white/70 group-hover:text-black dark:group-hover:text-brand-white transition-colors">
                    {account.type === "artist"
                      ? account.artistName || "Unknown Artist"
                      : account.displayName || "Unknown User"}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center">No other accounts to switch to.</p>
          )}
        </div>
        
        <button
          onClick={onClose}
          className="mt-10 py-3 rounded-2xl font-bold tracking-widest text-xs transition-all duration-200 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 active:scale-95 w-full"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default SwitchAccountsModal;